import { authorizedRequest } from "../../../services/apiClient";
import { buildQueryString } from "../../../utils/queryParams";
import { unwrapArrayResponse } from "../../../utils/apiHelpers";
import type {
  AddressPayload,
  OAuthConnection,
  User,
  UserStatus,
} from "@/types/auth";
import type { PaginationParams, SearchParams } from "@/types/api";

export type ListUsersParams = PaginationParams & SearchParams;

export interface AdminCreateUserPayload {
  email: string;
  name?: string;
  password: string;
  system_role?: User["system_role"];
}

export interface AdminUpdateUserPayload {
  email?: string;
  name?: string;
  system_role?: User["system_role"];
  address?: AddressPayload;
}

export interface AdminUpdateUserStatusPayload {
  status: Extract<UserStatus, "active" | "suspended" | "disabled">;
  reason?: string;
}

export async function listUsers(
  accessToken: string,
  params: ListUsersParams = {}
): Promise<User[]> {
  const path =
    "/admin/users" +
    buildQueryString({
      query: params.query,
      limit: params.limit,
      offset: params.offset
    });

  const response = await authorizedRequest<{ users: User[] }>(accessToken, path);
  return unwrapArrayResponse<User>(response, "users");
}

export async function createUser(
  accessToken: string,
  payload: AdminCreateUserPayload
): Promise<User> {
  return authorizedRequest<User>(accessToken, "/admin/users", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function updateUser(
  accessToken: string,
  userId: string,
  payload: AdminUpdateUserPayload
): Promise<User> {
  return authorizedRequest<User>(accessToken, `/admin/users/${userId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export async function getUser(accessToken: string, userId: string): Promise<User> {
  return authorizedRequest<User>(accessToken, `/admin/users/${userId}`);
}

export async function updateUserStatus(
  accessToken: string,
  userId: string,
  payload: AdminUpdateUserStatusPayload
): Promise<User> {
  return authorizedRequest<User>(accessToken, `/admin/users/${userId}/status`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export async function listUserOAuthConnections(
  accessToken: string,
  userId: string
): Promise<OAuthConnection[]> {
  const response = await authorizedRequest<{ connections: OAuthConnection[] }>(
    accessToken,
    `/admin/users/${userId}/oauth-connections`
  );
  return unwrapArrayResponse<OAuthConnection>(response, "connections");
}

export async function unlinkUserOAuthProvider(
  accessToken: string,
  userId: string,
  provider: string
): Promise<void> {
  await authorizedRequest(accessToken, `/admin/users/${userId}/oauth-connections/${provider}`, {
    method: "DELETE"
  });
}
